/**
 * iOS Sticky Offset Fix
 * Dynamically calculates and sets --sticky-offset CSS variable based on
 * sticky header and filter bar heights. Only active on iOS devices.
 */

import { isIOS } from './isIOS';

/**
 * Initialize the iOS sticky offset fix
 * @returns {Function} Cleanup function to remove listeners
 */
export function initIOSStickyOffsetFix() {
  // Only run on iOS
  if (!isIOS()) {
    return () => {}; // No-op cleanup
  }

  const update = () => {
    const header = document.querySelector('[data-sticky-header]');
    const filters = document.querySelector('[data-sticky-filters]');
    
    const headerHeight = header?.offsetHeight || 0;
    const filtersHeight = filters?.offsetHeight || 0;
    const totalOffset = headerHeight + filtersHeight;
    
    document.documentElement.style.setProperty('--sticky-offset', `${totalOffset}px`);
    
    if (process.env.NODE_ENV === 'development') {
      console.log('[iOS Sticky Fix] Updated offset:', {
        headerHeight,
        filtersHeight,
        totalOffset
      });
    }
  };

  // Initial update
  update();

  // iOS sometimes needs a second layout pass
  const delayedUpdate = setTimeout(update, 250);

  // Update on resize and orientation change
  const handleResize = () => {
    // Debounce resize events
    clearTimeout(handleResize.timeout);
    handleResize.timeout = setTimeout(update, 100);
  };

  window.addEventListener('resize', handleResize);
  window.addEventListener('orientationchange', update);

  // Cleanup function
  return () => {
    clearTimeout(delayedUpdate);
    clearTimeout(handleResize.timeout);
    window.removeEventListener('resize', handleResize);
    window.removeEventListener('orientationchange', update);
    document.documentElement.style.removeProperty('--sticky-offset');
  };
}

export default initIOSStickyOffsetFix;
